import { useMemo } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Download, Loader2, X } from "lucide-react";

import { Button } from "@app/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import { Switch } from "@app/components/ui/switch";
import { cn } from "@app/lib/utils";
import type {
  CovariateRequest,
  RegressionAnalysisRequest,
  RegressionResult,
  RegressionRow,
  StudyVariable,
} from "@app/types/study";

import { formatInterval, formatNumber, formatP } from "./format";
import { MethodsBox, WarningsList } from "./StudyNotes";
import { VariablePicker } from "./VariablePicker";

const ALL_LEVELS = "__all__";
const MAX_FACTORS = 12;

interface Props {
  variables: StudyVariable[];
  settings: RegressionAnalysisRequest | null;
  onSettings: (next: RegressionAnalysisRequest | null) => void;
  result: RegressionResult | null;
  loading: boolean;
  error: string | null;
  onExportCsv: () => void;
  exporting: boolean;
}

/** « Facteurs associés » — logistic regression, crude then adjusted odds ratios. */
export function RegressionAnalysis({
  variables,
  settings,
  onSettings,
  result,
  loading,
  error,
  onExportCsv,
  exporting,
}: Props) {
  const { t } = useLingui();
  const byKey = useMemo(() => new Map(variables.map((v) => [v.key, v])), [variables]);
  const outcome = settings ? byKey.get(settings.outcome) : undefined;

  const setOutcome = (key: string | null) => {
    if (!key) {
      onSettings(null);
      return;
    }
    onSettings({
      outcome: key,
      event_values: [],
      absent: false,
      covariates: settings?.covariates.filter((c) => c.key !== key) ?? [],
      alpha: settings?.alpha ?? 0.05,
    });
  };
  const set = (patch: Partial<RegressionAnalysisRequest>) => {
    if (settings) onSettings({ ...settings, ...patch });
  };
  const setCovariate = (index: number, patch: Partial<CovariateRequest>) =>
    set({
      covariates: (settings?.covariates ?? []).map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });

  return (
    <div className="space-y-5">
      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <span className="block text-xs font-medium text-slate-500">
              <Trans>Événement</Trans>
            </span>
            <VariablePicker
              mode="single"
              variables={variables}
              allow={(v) => v.type !== "numeric"}
              value={settings?.outcome ?? null}
              onChange={setOutcome}
              placeholder={t`Choisir l'événement étudié`}
              className="w-72"
            />
          </div>
          {settings && outcome && (
            <>
              <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
                <Switch checked={settings.absent} onCheckedChange={(absent) => set({ absent })} />
                <Trans>Absence de mention = non</Trans>
              </label>
              <div className="ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExportCsv}
                  disabled={!result || exporting}
                  className="gap-1.5 hover:border-primary/50"
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  <Trans>CSV</Trans>
                </Button>
              </div>
            </>
          )}
        </div>

        {settings && outcome && outcome.type !== "binary" && outcome.levels.length > 0 && (
          <div className="space-y-1.5">
            <span className="block text-xs font-medium text-slate-500">
              <Trans>Valeurs comptées comme l'événement</Trans>{" "}
              <span className="font-normal text-slate-400">
                <Trans>(aucune choisie : toute valeur hors absence)</Trans>
              </span>
            </span>
            <div className="flex flex-wrap gap-1.5">
              {outcome.levels.slice(0, 30).map((level) => {
                const active = settings.event_values.includes(level.value);
                return (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() =>
                      set({
                        event_values: active
                          ? settings.event_values.filter((v) => v !== level.value)
                          : [...settings.event_values, level.value],
                      })
                    }
                    className={cn(
                      "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                      active
                        ? "border-primary bg-primary text-white"
                        : "border-slate-200 text-slate-600 hover:border-primary/50",
                    )}
                  >
                    {level.label} <span className={active ? "text-white/70" : "text-slate-400"}>{level.n}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {settings && (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-slate-500">
                <Trans>Facteurs</Trans> ({settings.covariates.length}/{MAX_FACTORS})
              </span>
              {settings.covariates.length < MAX_FACTORS && (
                <VariablePicker
                  mode="single"
                  variables={variables}
                  allow={(v) => v.key !== settings.outcome && !settings.covariates.some((c) => c.key === v.key)}
                  value={null}
                  onChange={(key) => {
                    if (!key) return;
                    const variable = byKey.get(key);
                    const value =
                      variable?.type === "multi" ? (variable.levels[0]?.value ?? null) : null;
                    set({ covariates: [...settings.covariates, { key, value, absent: false }] });
                  }}
                  placeholder={t`Ajouter un facteur`}
                  className="h-8 w-56 text-xs"
                />
              )}
            </div>
            {settings.covariates.map((covariate, index) => {
              const variable = byKey.get(covariate.key);
              if (!variable) return null;
              const needsValue = variable.type === "multi";
              const choosesValue = ["categorical", "multi", "ordinal", "date"].includes(variable.type);
              return (
                <div
                  key={covariate.key}
                  className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 px-3 py-2"
                >
                  <span className="min-w-[10rem] text-sm font-medium text-slate-800">{variable.label}</span>
                  {choosesValue && (
                    <Select
                      value={covariate.value ?? ALL_LEVELS}
                      onValueChange={(value) => setCovariate(index, { value: value === ALL_LEVELS ? null : value })}
                    >
                      <SelectTrigger className="h-8 w-64 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {!needsValue && (
                          <SelectItem value={ALL_LEVELS}>{t`Toutes les modalités (comparées à la plus fréquente)`}</SelectItem>
                        )}
                        {variable.levels.slice(0, 40).map((level) => (
                          <SelectItem key={level.value} value={level.value}>
                            {t`Porte « ${level.label} »`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {variable.type !== "numeric" && (
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                      <Switch
                        checked={covariate.absent ?? false}
                        onCheckedChange={(absent) => setCovariate(index, { absent })}
                      />
                      <Trans>Absence de mention = non</Trans>
                    </label>
                  )}
                  <button
                    type="button"
                    className="ml-auto text-slate-400 hover:text-slate-700"
                    onClick={() => set({ covariates: settings.covariates.filter((_, i) => i !== index) })}
                    aria-label={t`Retirer ce facteur`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!settings && (
        <p className="text-sm text-slate-500">
          <Trans>
            Choisissez l'événement étudié — une complication, une reprise, une transfusion — puis les facteurs
            dont vous voulez mesurer l'association, chacun ajusté sur les autres.
          </Trans>
        </p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
      {!result && loading && (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {result && settings && (
        <div className={cn("space-y-4 transition-opacity", loading && "opacity-60")}>
          <ModelSummary result={result} />
          <WarningsList warnings={result.warnings} />
          {result.rows.length > 0 && (
            <>
              <ForestPlot rows={result.rows} />
              <OddsTable rows={result.rows} alpha={result.alpha} />
            </>
          )}
          <MethodsBox text={result.methods} />
        </div>
      )}
    </div>
  );
}

function ModelSummary({ result }: { result: RegressionResult }) {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const model = result.model;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Stat label={<Trans>Unités analysées</Trans>} value={`${model.n ?? "—"} / ${model.total ?? "—"}`} />
      <Stat label={<Trans>Événements</Trans>} value={String(model.events ?? "—")} />
      <Stat
        label={<Trans>Rapport de vraisemblance</Trans>}
        value={model.lr_p === null ? "—" : `p ${formatP(model.lr_p, locale)}`}
      />
      <Stat label={<Trans>Statistique C (AUC)</Trans>} value={formatNumber(model.auc, locale, 2)} />
    </div>
  );
}

function Stat({ label, value }: { label: React.ReactNode; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

function termLabel(row: RegressionRow): string {
  return row.level ? `${row.label} — ${row.level}` : row.label;
}

/**
 * Adjusted odds ratios on a log scale around 1, one line per factor. Significance is said in weight
 * — a filled marker and a darker line — never in a hue: green and red belong to a clinician's verdict.
 */
function ForestPlot({ rows }: { rows: RegressionRow[] }) {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const estimable = rows.filter((r) => r.adjusted.or !== null);
  const bounds = estimable.flatMap((r) => [r.adjusted.low ?? r.adjusted.or ?? 1, r.adjusted.high ?? r.adjusted.or ?? 1]);
  const lo = Math.max(0.05, Math.min(0.5, ...bounds) / 1.2);
  const hi = Math.min(20, Math.max(2, ...bounds) * 1.2);
  const width = 760;
  const rowH = 26;
  const labelW = 300;
  const plotW = width - labelW - 140;
  const height = estimable.length * rowH + 30;
  const x = (v: number) => labelW + ((Math.log(Math.min(hi, Math.max(lo, v))) - Math.log(lo)) / (Math.log(hi) - Math.log(lo))) * plotW;
  const ticks = [0.1, 0.25, 0.5, 1, 2, 4, 10].filter((v) => v >= lo && v <= hi);

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-4">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full min-w-[640px]" role="img">
        <line x1={x(1)} x2={x(1)} y1={0} y2={height - 24} stroke="#94A3B8" strokeDasharray="3 3" />
        {ticks.map((tick) => (
          <text key={tick} x={x(tick)} y={height - 8} textAnchor="middle" fontSize="11" fill="#64748B">
            {formatNumber(tick, locale, tick < 1 ? 2 : 0)}
          </text>
        ))}
        {estimable.map((row, index) => {
          const cy = index * rowH + 14;
          const { or, low, high } = row.adjusted;
          const strong = row.significant;
          return (
            <g key={`${row.factor}-${row.level ?? ""}`}>
              <text x={0} y={cy} dy="0.32em" fontSize="12" fill="#334155" fontWeight={strong ? 600 : 400}>
                {termLabel(row).slice(0, 44)}
              </text>
              {low !== null && high !== null && (
                <line x1={x(low)} x2={x(high)} y1={cy} y2={cy} stroke={strong ? "#1752B7" : "#94A3B8"} strokeWidth={2} />
              )}
              {/* An interval the axis cuts ends in an arrow: drawn flush, it read as ending there. */}
              {low !== null && low < lo && (
                <path
                  d={`M ${x(lo) + 6} ${cy - 4} L ${x(lo)} ${cy} L ${x(lo) + 6} ${cy + 4}`}
                  fill="none"
                  stroke={strong ? "#1752B7" : "#94A3B8"}
                  strokeWidth={2}
                />
              )}
              {high !== null && high > hi && (
                <path
                  d={`M ${x(hi) - 6} ${cy - 4} L ${x(hi)} ${cy} L ${x(hi) - 6} ${cy + 4}`}
                  fill="none"
                  stroke={strong ? "#1752B7" : "#94A3B8"}
                  strokeWidth={2}
                />
              )}
              {or !== null && (
                <rect
                  x={x(or) - 4}
                  y={cy - 4}
                  width={8}
                  height={8}
                  fill={strong ? "#1752B7" : "#FFFFFF"}
                  stroke={strong ? "#1752B7" : "#64748B"}
                />
              )}
              <text x={labelW + plotW + 12} y={cy} dy="0.32em" fontSize="11" fill="#475569">
                {formatNumber(or, locale, 2)} {formatInterval(low, high, locale)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function OddsTable({ rows, alpha }: { rows: RegressionRow[]; alpha: number }) {
  const { i18n, t } = useLingui();
  const locale = i18n.locale;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500">
          <tr>
            <th className="px-4 py-2.5 text-left font-medium">
              <Trans>Facteur</Trans>
            </th>
            <th className="px-3 py-2.5 text-right font-medium">n</th>
            <th className="px-3 py-2.5 text-right font-medium">
              <Trans>Événements</Trans>
            </th>
            <th className="px-3 py-2.5 text-left font-medium">
              <Trans>OR brut (IC 95 %)</Trans>
            </th>
            <th className="px-3 py-2.5 text-right font-medium">p</th>
            <th className="px-3 py-2.5 text-left font-medium">
              <Trans>OR ajusté (IC 95 %)</Trans>
            </th>
            <th className="px-3 py-2.5 text-right font-medium">p</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.factor}-${row.level ?? ""}`} className="border-t border-slate-200">
              <td className="px-4 py-2.5">
                <div className="font-medium text-slate-900">{row.label}</div>
                <div className="text-2xs text-slate-400">
                  {row.numeric
                    ? t`par unité`
                    : row.reference
                      ? t`${row.level ?? ""} contre ${row.reference}`
                      : (row.level ?? "")}
                </div>
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums">{row.n ?? "—"}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{row.events ?? "—"}</td>
              <td className="px-3 py-2.5 tabular-nums">
                {formatNumber(row.crude.or, locale, 2)}{" "}
                <span className="text-slate-400">{formatInterval(row.crude.low, row.crude.high, locale)}</span>
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{formatP(row.crude.p, locale)}</td>
              <td className="px-3 py-2.5 tabular-nums">
                {formatNumber(row.adjusted.or, locale, 2)}{" "}
                <span className="text-slate-400">{formatInterval(row.adjusted.low, row.adjusted.high, locale)}</span>
              </td>
              <td
                className={cn(
                  "px-3 py-2.5 text-right tabular-nums",
                  (row.adjusted.p ?? 1) < alpha ? "font-semibold text-primary" : "text-slate-600",
                )}
              >
                {formatP(row.adjusted.p, locale)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
